/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent } from 'react'
import styled, { useTheme } from 'styled-components/native'

import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { Button } from 'ui/designSystem/Button/Button'
import { MODAL_DETAILED_CLOSE_BUTTON_CONTAINER_ID } from 'ui/designSystem/ModalDetailed/constants'
import { ModalDetailedHeaderProps } from 'ui/designSystem/ModalDetailed/types'
import { ArrowPrevious } from 'ui/svg/icons/ArrowPrevious'
import { CloseFilled } from 'ui/svg/icons/CloseFilled'
import { Typo } from 'ui/theme'
import { setTextSemantic } from 'ui/theme/typographyAttrs/setTextSemantic'

export const ModalDetailedHeader: FunctionComponent<ModalDetailedHeaderProps> = ({
  title,
  titleId,
  onClose,
  onGoBack,
  closeAccessibilityLabel = 'Quitter',
  backAccessibilityLabel = 'Retourner à l’étape précédente',
}) => {
  const { isDesktopViewport } = useTheme()

  return (
    <HeaderContainer accessibilityRole={AccessibilityRole.HEADER}>
      <CloseButtonContainer nativeID={MODAL_DETAILED_CLOSE_BUTTON_CONTAINER_ID}>
        <Button
          icon={CloseFilled}
          iconButton
          accessibilityLabel={closeAccessibilityLabel}
          variant="tertiary"
          color="neutral"
          onPress={onClose}
        />
      </CloseButtonContainer>

      {onGoBack ? (
        <BackButtonContainer>
          <Button
            icon={ArrowPrevious}
            iconButton
            accessibilityLabel={backAccessibilityLabel}
            variant="tertiary"
            color="neutral"
            onPress={onGoBack}
          />
        </BackButtonContainer>
      ) : null}

      <TitleContainer hasBackButton={!!onGoBack}>
        {isDesktopViewport ? (
          <DesktopTitle nativeID={titleId} {...setTextSemantic('h1')}>
            {title}
          </DesktopTitle>
        ) : (
          <MobileTitle nativeID={titleId} {...setTextSemantic('h1')}>
            {title}
          </MobileTitle>
        )}
      </TitleContainer>
    </HeaderContainer>
  )
}

const HeaderContainer = styled.View(({ theme }) => ({
  position: 'relative',
  flexShrink: 0,
  width: '100%',
  minHeight: theme.designSystem.size.icon.l,
  justifyContent: 'center',
  marginBottom: theme.designSystem.size.spacing.l,
}))

const HeaderButtonContainer = styled.View(({ theme }) => ({
  position: 'absolute',
  top: -theme.designSystem.size.spacing.s,
  zIndex: theme.zIndex.header,
}))

const CloseButtonContainer = styled(HeaderButtonContainer)(({ theme }) => ({
  right: -theme.designSystem.size.spacing.s,
}))

const BackButtonContainer = styled(HeaderButtonContainer)(({ theme }) => ({
  left: -theme.designSystem.size.spacing.s,
}))

const TitleContainer = styled.View<{ hasBackButton: boolean }>(({ theme, hasBackButton }) => ({
  flexShrink: 1,
  width: '100%',
  paddingLeft:
    hasBackButton || !theme.isDesktopViewport ? theme.designSystem.size.icon.l : undefined,
  paddingRight: theme.designSystem.size.icon.l,
}))

const DesktopTitle = styled(Typo.Title2)(({ theme }) => ({
  color: theme.designSystem.color.text.default,
  textAlign: 'left',
}))

const MobileTitle = styled(Typo.Title3)(({ theme }) => ({
  color: theme.designSystem.color.text.default,
  textAlign: 'center',
}))
